import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 13 — Client Messages.
 * Mirrors feedbackQueries.js/fileQueries.js's `request()` error contract
 * exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const mqk = {
  threads: (wrkId) => ["message-threads", wrkId],
  thread: (wrkId, threadId) => ["message-thread", wrkId, threadId],
};

export function useMessageThreads(workspaceId) {
  return useQuery({
    queryKey: mqk.threads(workspaceId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/messages/threads`),
    refetchInterval: 15000,
  });
}

export function useThreadMessages(workspaceId, threadId) {
  return useQuery({
    queryKey: mqk.thread(workspaceId, threadId),
    enabled: !!workspaceId && !!threadId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/messages/threads/${threadId}`),
    refetchInterval: 8000,
  });
}

export function useSaveDraft(workspaceId, threadId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/messages/threads/${threadId}/draft`, { method: "PUT", body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mqk.thread(workspaceId, threadId) }),
  });
}

export function useSendMessage(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/messages/send`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mqk.threads(workspaceId) });
      qc.invalidateQueries({ queryKey: ["message-thread", workspaceId] });
    },
  });
}
